/**
 * Skill frontmatter identity helpers.
 *
 * These helpers keep custom-named installs traceable to their source template
 * without coupling that parsing/writing logic to the skill command runner.
 */

/**
 * Brand-neutral frontmatter key that records which template a SKILL.md was
 * rendered from. Stamped only when a skill is installed under a custom name
 * (`--name`), so `sync`/`diff` can map a renamed install back to its template
 * for version-compare and update.
 */
export const SKILL_SOURCE_KEY = "skill-source";

/**
 * Split a SKILL.md into its leading YAML frontmatter block and the body. The
 * frontmatter is the block between the first `---` line and the next `---`
 * line. Returns `null` when the content has no recognizable frontmatter, so
 * callers can skip identity rewriting rather than corrupt the file.
 */
function splitFrontmatter(
  content: string
): { frontmatter: string; body: string } | null {
  const lines = content.split("\n");
  if (lines[0] !== "---") return null;
  for (let i = 1; i < lines.length; i++) {
    if (lines[i] === "---") {
      return {
        frontmatter: lines.slice(1, i).join("\n"),
        // Keep the closing `---` and everything after it in the body so a
        // round-trip with `applyInstallIdentity` reproduces the file exactly.
        body: lines.slice(i).join("\n"),
      };
    }
  }
  return null;
}

/**
 * Read a single `key: value` field out of a frontmatter block. Returns `null`
 * when the key is absent. Used to recover an installed skill's `name:` /
 * `skill-source:` for the collision guard.
 */
function readFrontmatterField(frontmatter: string, key: string): string | null {
  for (const line of frontmatter.split("\n")) {
    const match = line.match(/^([A-Za-z0-9_-]+):\s*(.*)$/);
    if (match && match[1] === key) return match[2].trim();
  }
  return null;
}

/**
 * Derive the template key an already-installed SKILL.md was rendered from:
 * the `skill-source:` stamp when present (custom-named install), otherwise the
 * `name:` field (a default install, where name === template key). `null` when
 * neither is present.
 */
export function installedSourceKey(content: string): string | null {
  const parts = splitFrontmatter(content);
  if (!parts) return null;
  return (
    readFrontmatterField(parts.frontmatter, SKILL_SOURCE_KEY) ??
    readFrontmatterField(parts.frontmatter, "name")
  );
}

/**
 * Rewrite a rendered SKILL.md so it installs cleanly under `installName`:
 * the frontmatter `name:` is set to `installName` (agents require `name:` to
 * match the install directory), and — only when the name differs from the
 * template key — a `skill-source: <templateKey>` line is stamped so
 * `sync`/`diff` can still track the install.
 */
export function applyInstallIdentity(
  content: string,
  installName: string,
  templateKey: string
): string {
  if (installName === templateKey) return content;

  const parts = splitFrontmatter(content);
  if (!parts) return content;

  const out: string[] = [];
  let wroteName = false;
  let wroteSource = false;
  for (const line of parts.frontmatter.split("\n")) {
    const match = line.match(/^([A-Za-z0-9_-]+):\s*(.*)$/);
    const key = match?.[1];
    if (key === "name") {
      out.push(`name: ${installName}`);
      out.push(`${SKILL_SOURCE_KEY}: ${templateKey}`);
      wroteName = true;
      wroteSource = true;
    } else if (key === SKILL_SOURCE_KEY) {
      // Re-stamp from the template key rather than trusting a stale value.
      if (!wroteSource) {
        out.push(`${SKILL_SOURCE_KEY}: ${templateKey}`);
        wroteSource = true;
      }
    } else {
      out.push(line);
    }
  }
  // Defensive: frontmatter missing `name:` / `skill-source:` still gets them.
  if (!wroteName) out.unshift(`name: ${installName}`);
  if (!wroteSource) {
    const nameIdx = out.findIndex((l) => l.startsWith("name:"));
    out.splice(nameIdx >= 0 ? nameIdx + 1 : 0, 0, `${SKILL_SOURCE_KEY}: ${templateKey}`);
  }

  // Reassemble: leading `---`, rewritten frontmatter, then the original body
  // (which already starts with its own closing `---` fence).
  return `---\n${out.join("\n")}\n${parts.body}`;
}
